// S-2213: Reliable video details provider with retry logic and safe fallbacks
import { supabase } from '@/integrations/supabase/client';
import { VideoRecord } from '@/lib/videos';

export interface VideoDetailsResult {
  data: VideoRecord | null;
  error: VideoDetailsError | null;
  fromFallback: boolean;
}

export class VideoDetailsError extends Error {
  public readonly code: string;
  public readonly retryable: boolean;
  public readonly context: Record<string, any>;

  constructor(
    code: string,
    message: string,
    retryable: boolean = true,
    context: Record<string, any> = {}
  ) {
    super(message);
    this.name = 'VideoDetailsError';
    this.code = code;
    this.retryable = retryable;
    this.context = context;
    Object.setPrototypeOf(this, VideoDetailsError.prototype);
  }

  static authError(originalError: any): VideoDetailsError {
    return new VideoDetailsError(
      'AUTH_ERROR',
      'Authentication failed - please sign in again',
      false,
      { originalError: String(originalError) }
    );
  }

  static networkError(originalError: any): VideoDetailsError {
    return new VideoDetailsError(
      'NETWORK_ERROR',
      'Network connection failed - please check your internet connection',
      true,
      { originalError: String(originalError) }
    );
  }

  static notFound(videoId: string): VideoDetailsError {
    return new VideoDetailsError(
      'VIDEO_NOT_FOUND',
      `Video ${videoId} not found or access denied`,
      false,
      { videoId }
    );
  }

  static databaseError(originalError: any): VideoDetailsError {
    return new VideoDetailsError(
      'DATABASE_ERROR',
      'Database query failed - please try again',
      true,
      { originalError: String(originalError) }
    );
  }

  static clientError(originalError: any, videoId: string): VideoDetailsError {
    return new VideoDetailsError(
      'CLIENT_ERROR',
      `Invalid request for video ${videoId}: ${originalError.message}`,
      false, // Non-retryable
      { originalError: String(originalError), videoId }
    );
  }

  static processingIncomplete(videoId: string, status: string): VideoDetailsError {
    return new VideoDetailsError(
      'PROCESSING_INCOMPLETE',
      `Video is still processing (status: ${status}) - please wait and try again`,
      true,
      { videoId, status }
    );
  }
}

export interface RetryConfig {
  maxAttempts: number;
  baseDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2
};

/**
 * S-2213: Enhanced video details provider with retry logic and safe fallbacks
 */
export class VideoDetailsProvider {
  private retryConfig: RetryConfig;

  constructor(retryConfig: Partial<RetryConfig> = {}) {
    this.retryConfig = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
  }

  /**
   * Get video details with retry logic and fallback
   * AC1: Returns non-null via retry(backoff) + signed policy headers
   * AC2: Safe fallback value if details remain unavailable
   */
  async getVideoDetails(videoId: string): Promise<VideoDetailsResult> {
    console.log(`[VideoDetailsProvider] Fetching details for video: ${videoId}`);

    let lastError: VideoDetailsError | null = null;

    // Retry loop with exponential backoff
    for (let attempt = 1; attempt <= this.retryConfig.maxAttempts; attempt++) {
      try {
        console.log(`[VideoDetailsProvider] Attempt ${attempt}/${this.retryConfig.maxAttempts}`);

        const result = await this.fetchVideoDetailsOnce(videoId);
        
        if (result.data) {
          console.log(`[VideoDetailsProvider] Success on attempt ${attempt}`);
          return { data: result.data, error: null, fromFallback: false };
        }

        // If no data but no error, treat as not found
        if (!result.error) {
          lastError = VideoDetailsError.notFound(videoId);
        } else {
          lastError = result.error;
        }

        // Don't retry non-retryable errors
        if (!lastError.retryable) {
          console.log(`[VideoDetailsProvider] Non-retryable error: ${lastError.code}`);
          break;
        }

        // Calculate delay for next attempt
        if (attempt < this.retryConfig.maxAttempts) {
          const delay = Math.min(
            this.retryConfig.baseDelayMs * Math.pow(this.retryConfig.backoffMultiplier, attempt - 1),
            this.retryConfig.maxDelayMs
          );
          console.log(`[VideoDetailsProvider] Retrying in ${delay}ms...`);
          await this.sleep(delay);
        }

      } catch (error) {
        console.error(`[VideoDetailsProvider] Unexpected error on attempt ${attempt}:`, error);
        lastError = VideoDetailsError.networkError(error);
        
        if (attempt < this.retryConfig.maxAttempts) {
          const delay = Math.min(
            this.retryConfig.baseDelayMs * Math.pow(this.retryConfig.backoffMultiplier, attempt - 1),
            this.retryConfig.maxDelayMs
          );
          await this.sleep(delay);
        }
      }
    }

    // All retries failed - return safe fallback
    console.warn(`[VideoDetailsProvider] All retries failed, returning fallback for video: ${videoId}`);
    const fallbackData = this.createFallbackVideoDetails(videoId, lastError);
    
    return {
      data: fallbackData,
      error: lastError,
      fromFallback: true
    };
  }

  /**
   * Single attempt to fetch video details
   */
  private async fetchVideoDetailsOnce(videoId: string): Promise<{ data: VideoRecord | null; error: VideoDetailsError | null }> {
    try {
      // Check authentication first
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        return { data: null, error: VideoDetailsError.authError(authError) };
      }

      // Fetch video with only existing core columns (S-2224: avoid 400 errors)
      const { data, error } = await supabase
        .from('videos')
        .select('id,user_id,mux_asset_id,mux_playback_id,playback_policy,status,duration,width,height,created_at,updated_at,deleted_at')
        .eq('id', videoId)
        .eq('user_id', user.id)
        .single();

      if (error) {
        // Classify Supabase errors
        if (error.code === 'PGRST116') {
          return { data: null, error: VideoDetailsError.notFound(videoId) };
        }
        
        // Handle client errors (400-499) as non-retryable
        if (error.code === '400' || error.code === '22P02' || error.code === '42703') {
          return { data: null, error: VideoDetailsError.clientError(error, videoId) };
        }
        
        return { data: null, error: VideoDetailsError.databaseError(error) };
      }

      if (!data) {
        return { data: null, error: VideoDetailsError.notFound(videoId) };
      }

      // Validate video is ready for use
      if (data.status !== 'ready' || !data.mux_playback_id) {
        return { 
          data: null, 
          error: VideoDetailsError.processingIncomplete(videoId, data.status || 'unknown') 
        };
      }

      return { data, error: null };

    } catch (error) {
      console.error('[VideoDetailsProvider] Fetch error:', error);
      return { data: null, error: VideoDetailsError.networkError(error) };
    }
  }

  /**
   * AC2: Create safe fallback video details when all retries fail
   */
  private createFallbackVideoDetails(videoId: string, error: VideoDetailsError | null): VideoRecord {
    console.log(`[VideoDetailsProvider] Creating fallback details for video: ${videoId}`);
    
    return {
      id: videoId,
      user_id: 'unknown',
      mux_asset_id: null,
      mux_playback_id: null,
      playback_policy: 'signed',
      status: 'error',
      duration: null,
      width: null,
      height: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      deleted_at: null
    };
  }

  /**
   * Utility: Sleep for specified milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if video details are from fallback (for UI messaging)
   */
  static isFallbackVideo(video: VideoRecord): boolean {
    return video.status === 'error' || video.user_id === 'unknown';
  }

  /**
   * Get user-friendly error message for UI display
   */
  static getErrorMessage(error: VideoDetailsError): string {
    switch (error.code) {
      case 'AUTH_ERROR':
        return 'Please sign in to access this video.';
      case 'VIDEO_NOT_FOUND':
        return 'This video was not found or you don\'t have permission to view it.';
      case 'PROCESSING_INCOMPLETE':
        return 'This video is still being processed. Please wait a few minutes and try again.';
      case 'NETWORK_ERROR':
        return 'Connection failed. Please check your internet connection and try again.';
      case 'DATABASE_ERROR':
        return 'Unable to load video details. Please try again in a moment.';
      case 'CLIENT_ERROR':
        return 'Invalid video request. Please check the video ID and try again.';
      default:
        return 'Unable to load video details. Please try again.';
    }
  }

  /**
   * Get suggested action for UI display
   */
  static getSuggestedAction(error: VideoDetailsError): string {
    switch (error.code) {
      case 'AUTH_ERROR':
        return 'Sign In';
      case 'VIDEO_NOT_FOUND':
        return 'Go to Library';
      case 'PROCESSING_INCOMPLETE':
        return 'Try Again';
      case 'NETWORK_ERROR':
      case 'DATABASE_ERROR':
        return 'Retry';
      case 'CLIENT_ERROR':
        return 'Go to Library';
      default:
        return 'Try Again';
    }
  }
}

// Export singleton instance
export const videoDetailsProvider = new VideoDetailsProvider();

// Export convenience function
export async function getVideoDetailsReliably(videoId: string): Promise<VideoDetailsResult> {
  return videoDetailsProvider.getVideoDetails(videoId);
}
